import { IFilterCriteria } from '@forwardflow/features/campaign-filter/interfaces/campaign-filter.types'

export type TCampaignKind = 'new-lead' | 'revive' | 'activity'
export type TLeadTypeId = 'buyer' | 'seller' | 'rental'

/** A campaign the backend can create in one call, plus the words on its card. */
export interface IStarterCampaign {
   id: string
   template: 'new-leads' | 'revive' | 'pond' | 'source' | 'custom'
   title: string
   description: string
   campaignName: string
   source?: string
   criteria: IFilterCriteria[]
   campaignType: TCampaignKind[]
   leadTypes: TLeadTypeId[]
   /** Minutes to wait before the first call to a brand-new lead. */
   bufferTime: number
   /** A count we already know (source totals, pond members); refined by the API. */
   knownCount?: number
   recommended?: boolean
}

export interface ISourceLike {
   leadSource: string
   totalLeads?: number
   historicalTotalLeads?: number
}
export interface IPondLike {
   pondId: number
   pondName: string
   totalMembers?: number
}
export interface IRecommendedLike {
   title?: string
   source?: string
   totalLeads?: number
}

export const NEW_LEAD_BUFFER_MINUTES = 2
const REVIVE_MIN_HISTORICAL = 200
const DEFAULT_LEAD_TYPES: TLeadTypeId[] = ['buyer', 'seller']

const criterion = (
   field: IFilterCriteria['field'],
   value: Array<string | number>,
   operator: IFilterCriteria['operator'] = 'CONTAINS_ANY'
): IFilterCriteria => ({ field, logic: 'AND', value, operator })

const newLeadsFrom = (
   source: ISourceLike,
   recommended: boolean
): IStarterCampaign => ({
   id: `new:${source.leadSource}`,
   template: 'new-leads',
   title: `New ${source.leadSource} leads`,
   description: `Calls every new ${source.leadSource} lead within ${NEW_LEAD_BUFFER_MINUTES} minutes of arrival and books the appointment for you.`,
   campaignName: `New ${source.leadSource} leads`,
   source: source.leadSource,
   criteria: [criterion('source', [source.leadSource])],
   campaignType: ['new-lead'],
   leadTypes: DEFAULT_LEAD_TYPES,
   bufferTime: NEW_LEAD_BUFFER_MINUTES,
   knownCount: source.totalLeads,
   recommended,
})

const reviveAll = (historical: number): IStarterCampaign => ({
   id: 'revive:all',
   template: 'revive',
   title: 'Revive cold leads',
   description:
      'Works the older leads that never booked, a few each day, and hands you the ones that are ready again.',
   campaignName: 'Revive cold leads',
   criteria: [],
   campaignType: ['revive'],
   leadTypes: DEFAULT_LEAD_TYPES,
   bufferTime: 0,
   knownCount: historical || undefined,
})

const pondLeads = (pond: IPondLike): IStarterCampaign => ({
   id: `pond:${pond.pondId}`,
   template: 'pond',
   title: `${pond.pondName} leads`,
   description: `Calls the leads sitting in your ${pond.pondName} that nobody has claimed yet.`,
   campaignName: `${pond.pondName} leads`,
   criteria: [criterion('assigned_pond_id', [pond.pondId])],
   campaignType: ['new-lead'],
   leadTypes: DEFAULT_LEAD_TYPES,
   bufferTime: NEW_LEAD_BUFFER_MINUTES,
   knownCount: pond.totalMembers,
})

/**
 * The three starter cards for an account, best first.
 *
 * 1. New leads from the top source: the first recommended campaign, else the
 *    source with the most leads in the last 90 days.
 * 2. Revive, when the account has a real backlog; else the biggest pond;
 *    else the second source.
 * 3. Whichever of the above is left, else the next source.
 */
export const buildStarterCampaigns = ({
   recommended = [],
   sources = [],
   ponds = [],
}: {
   recommended?: IRecommendedLike[]
   sources?: ISourceLike[]
   ponds?: IPondLike[]
}): IStarterCampaign[] => {
   const bySize = sources
      .filter((s) => s.leadSource)
      .sort((a, b) => (b.totalLeads || 0) - (a.totalLeads || 0))
   const recommendedName = recommended
      .map((r) => r.source || r.title)
      .find((name) => name && bySize.some((s) => s.leadSource === name))
   const ordered = recommendedName
      ? [
           bySize.find((s) => s.leadSource === recommendedName) as ISourceLike,
           ...bySize.filter((s) => s.leadSource !== recommendedName),
        ]
      : bySize

   const historical = sources.reduce(
      (sum, s) => sum + (s.historicalTotalLeads || 0),
      0
   )
   const biggestPond = [...ponds]
      .filter((p) => (p.totalMembers || 0) > 0)
      .sort((a, b) => (b.totalMembers || 0) - (a.totalMembers || 0))[0]

   const candidates: IStarterCampaign[] = []
   const sourceQueue = [...ordered]
   const nextSource = (recommendedFlag: boolean) => {
      const s = sourceQueue.shift()
      return s ? newLeadsFrom(s, recommendedFlag) : null
   }

   const first = nextSource(Boolean(recommendedName))
   if (first) candidates.push(first)

   const extras: IStarterCampaign[] = []
   if (historical >= REVIVE_MIN_HISTORICAL) extras.push(reviveAll(historical))
   if (biggestPond) extras.push(pondLeads(biggestPond))

   while (candidates.length < 3) {
      const extra = extras.shift()
      if (extra) {
         candidates.push(extra)
         continue
      }
      const s = nextSource(false)
      if (!s) break
      candidates.push(s)
   }
   return candidates
}

/* ---------- Plain-language description → filters ---------- */

export interface IVocabulary {
   sources: string[]
   stages: Array<{ id: string; name: string }>
   tags: string[]
   ponds: IPondLike[]
}

export interface ICompiled {
   criteria: IFilterCriteria[]
   campaignType: TCampaignKind[]
   leadTypes: TLeadTypeId[]
   /** What we understood, one phrase per match, for the chips. */
   understood: string[]
   /** True when nothing in the text matched the account. */
   nothingMatched: boolean
}

const NEGATION = /\b(not|no|never|except|excluding|exclude|without|skip|minus|but not|aren't|isn't|don't)\b[^.,;]{0,24}$/i
const REVIVE_WORDS = /\b(old|older|cold|dead|stale|dormant|revive|reactivate|re-engage|haven'?t|never booked|months?|years?|ago|backlog|past)\b/i
const ACTIVITY_WORDS = /\b(just|recently|viewed|looked|opened|clicked|activity|engaged|active again|came back|returned)\b/i

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** Find every occurrence of a name and whether it is negated. */
const findMentions = (text: string, name: string) => {
   const out: { negated: boolean }[] = []
   if (!name.trim()) return out
   const re = new RegExp(`(^|[^a-z0-9])${escape(name.toLowerCase())}(?=$|[^a-z0-9])`, 'g')
   const lower = text.toLowerCase()
   let m: RegExpExecArray | null
   while ((m = re.exec(lower))) {
      const before = lower.slice(Math.max(0, m.index - 30), m.index + m[1].length)
      out.push({ negated: NEGATION.test(before) })
   }
   return out
}

/**
 * Turn "Zillow buyers not in my Sphere" into the filters the backend takes.
 * Only names that exist in the account are used; the lead types and campaign
 * kind come from ordinary words. Keyword-based, so a Claude compile can
 * replace it without changing what the screen does with the result.
 */
export const compileDescription = (
   text: string,
   vocab: IVocabulary
): ICompiled => {
   const include: Record<string, Array<string | number>> = {}
   const exclude: Record<string, Array<string | number>> = {}
   const understood: string[] = []

   // Longest names first so "Zillow Flex" wins over "Zillow".
   const byLength = <T,>(items: T[], name: (t: T) => string) =>
      [...items].sort((a, b) => name(b).length - name(a).length)

   let scanned = text
   const consume = (name: string) => {
      scanned = scanned.replace(new RegExp(escape(name), 'ig'), ' ')
   }

   const addMatch = (
      field: IFilterCriteria['field'],
      label: string,
      value: string | number,
      name: string
   ) => {
      const mentions = findMentions(scanned, name)
      if (!mentions.length) return
      const negated = mentions.every((m) => m.negated)
      const bucket = negated ? exclude : include
      bucket[field] = [...(bucket[field] || []), value]
      understood.push(`${negated ? 'not ' : ''}${label}`)
      consume(name)
   }

   byLength(vocab.sources, (s) => s).forEach((s) => addMatch('source', `from ${s}`, s, s))
   byLength(vocab.stages, (s) => s.name).forEach((s) =>
      addMatch('stage', `in ${s.name}`, s.id, s.name)
   )
   byLength(vocab.tags, (t) => t).forEach((t) => addMatch('tags', `tagged ${t}`, t, t))
   byLength(vocab.ponds, (p) => p.pondName).forEach((p) =>
      addMatch('assigned_pond_id', `in ${p.pondName}`, p.pondId, p.pondName)
   )

   const leadTypes: TLeadTypeId[] = []
   if (/\bbuyers?\b/i.test(scanned)) leadTypes.push('buyer')
   if (/\bsellers?\b|\blistings?\b|\bexpired\b|\bfsbo\b/i.test(scanned)) leadTypes.push('seller')
   if (/\brent(al|ers?|ing)?\b|\btenants?\b/i.test(scanned)) leadTypes.push('rental')
   if (leadTypes.length) understood.push(leadTypes.join(' + '))

   const campaignType: TCampaignKind[] = REVIVE_WORDS.test(scanned)
      ? ['revive']
      : ACTIVITY_WORDS.test(scanned)
        ? ['activity']
        : ['new-lead']
   if (campaignType[0] === 'revive') understood.push('older leads')
   if (campaignType[0] === 'activity') understood.push('recent activity')

   const criteria: IFilterCriteria[] = [
      ...Object.entries(include).map(([field, value]) =>
         criterion(field as IFilterCriteria['field'], value)
      ),
      ...Object.entries(exclude).map(([field, value]) =>
         criterion(field as IFilterCriteria['field'], value, 'NOT_CONTAINS_ANY')
      ),
   ]

   return {
      criteria,
      campaignType,
      leadTypes: leadTypes.length ? leadTypes : DEFAULT_LEAD_TYPES,
      understood,
      nothingMatched: understood.length === 0,
   }
}

/** A campaign built from a description or the advanced filters. */
export const customCampaign = (
   compiled: Pick<ICompiled, 'criteria' | 'campaignType' | 'leadTypes'>,
   name: string
): IStarterCampaign => ({
   id: 'custom',
   template: 'custom',
   title: name,
   description: '',
   campaignName: name,
   source: compiled.criteria.find((c) => c.field === 'source' && c.operator === 'CONTAINS_ANY')
      ?.value[0] as string | undefined,
   criteria: compiled.criteria,
   campaignType: compiled.campaignType,
   leadTypes: compiled.leadTypes,
   bufferTime: compiled.campaignType.includes('new-lead') ? NEW_LEAD_BUFFER_MINUTES : 0,
})

/** A short campaign name for a description, from what was understood. */
export const nameFor = (compiled: ICompiled, fallback = 'Custom campaign') => {
   const parts = compiled.understood.slice(0, 3).map((p) =>
      p.replace(/^((from|in|tagged|not)\s+)+/i, '')
   )
   const base = parts.length ? parts.join(' · ') : fallback
   return base.length > 60 ? `${base.slice(0, 57)}…` : base
}
